/**
 * Create an organisation workspace (CLAUDE.md 11.1, 11.5; ADR-0002).
 *
 * The path this exists for is the common one: an engineer starts alone, then forms or
 * joins a practice. Until now the application had no way to make a second workspace
 * at all, which meant the personal-workspace model was a dead end rather than a
 * starting point.
 *
 * The dialog explains what an organisation *is* in one sentence, because "create a
 * workspace" is meaningless to someone who has never been shown the concept -- and
 * ADR-0002's whole promise is that they never were.
 *
 * On success it refreshes the membership list, switches into the new workspace, and
 * lands on its Members section: the first thing anyone wants after creating a firm's
 * workspace is to invite the firm.
 */
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useCreateWorkspace } from '@/api/workspaces'
import { useAuth } from '@/auth/AuthProvider'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'

export function CreateOrganisationDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { refreshWorkspaces, switchWorkspace } = useAuth()
  const create = useCreateWorkspace()

  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim()) return
    setError(null)
    try {
      const workspace = await create.mutateAsync({
        name: name.trim(),
        locale_default: i18n.language.slice(0, 2),
      })
      // The list lives in AuthProvider, not in the query cache, so it is refreshed
      // before the switch -- otherwise the switcher would show a workspace the
      // application does not yet know the user belongs to.
      await refreshWorkspaces()
      switchWorkspace(workspace.id)
      setName('')
      onOpenChange(false)
      navigate('/settings/members')
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.localised : String(caught))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setError(null)
        onOpenChange(next)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('workspace.create_org')}</DialogTitle>
          <DialogDescription>{t('workspace.create_org_hint')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {error && (
            <p
              role="alert"
              className="rounded-md bg-[var(--status-fail-bg)] p-3 text-sm text-[var(--status-fail)]"
            >
              {error}
            </p>
          )}

          <Field>
            <FieldLabel>{t('workspace.create_org_name')}</FieldLabel>
            <FieldInput
              required
              // Inside a modal the user opened on purpose: moving focus to the first
              // field is expected, not a focus steal.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <DialogFooter>
            <Button type="button" onClick={() => onOpenChange(false)}>
              {t('action.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={create.isPending}>
              <Building2 size={16} aria-hidden />
              {t('action.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
